import statisticDefinitions from './statistics.js';
import { setParallaxTranslation } from './parallaxMotion.js';

const statisticModule = {
  init(definitions = statisticDefinitions) {
    const container = document.querySelector('.pages-container');
    if (!container) return;
    const strip = container.querySelector('.pages-inner');
    if (!strip) return;
    const desktop = matchMedia('(min-width: 768px)');
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    const defaultParallax = Number.parseFloat(getComputedStyle(document.documentElement)
      .getPropertyValue('--statistic-parallax-amount')) || 0.12;
    const pages = Array.from(container.querySelectorAll('.page'));
    const entries = [];
    for (const definition of definitions) {
      const page = pages.find(element => element.dataset.id === String(definition.page));
      if (!page) continue;
      const module = document.createElement('div');
      module.className = 'statistic-module statistic-module--banner';
      module.dataset.page = String(definition.page);
      module.classList.toggle('statistic-module--mobile-stacked', definition.mobile?.stacked === true);
      const parts = [];
      const content = definition.content || [];
      for (const item of content) {
        const property = item.type;
        const value = item.value;
        if (value == null || !['number', 'description', 'text'].includes(property)) continue;
        if (property === 'number' && !Number.isFinite(value)) continue;
        const part = document.createElement('p');
        part.className = `statistic-module__${property}${item.emphasis ? ' is-emphasized' : ''}`;
        if (property === 'number') {
          const finalText = `${item.prefix || ''}${value}${item.suffix || ''}`;
          part.setAttribute('aria-label', finalText);
          const counter = document.createElement('span');
          counter.className = 'statistic-module__count';
          counter.setAttribute('aria-hidden', 'true');
          counter.textContent = `${item.prefix || ''}0`;
          part.append(counter);
          if (item.suffix) {
            const suffix = document.createElement('span');
            suffix.className = 'statistic-module__suffix';
            suffix.setAttribute('aria-hidden', 'true');
            suffix.textContent = item.suffix;
            part.append(suffix);
          }
        } else {
          part.textContent = String(value).replace(/<br\s*\/?\s*>/gi, '\n');
        }
        module.append(part);
        parts.push({ property, value, item, part });
      }
      if (!parts.length) continue;
      const pinnedStage = page.querySelector('.video-over-video-stage');
      const anchor = pinnedStage || page.querySelector('.filmstrip-stage') || page;
      (pinnedStage || strip).append(module);
      entries.push({ definition, page, anchor, pinnedStage, module, parts });
    }
    const clamp = value => Math.max(0, Math.min(1, value));
    const update = () => {
      const viewport = container.getBoundingClientRect();
      const stripBounds = strip.getBoundingClientRect();
      for (const { definition, page, anchor, pinnedStage, module, parts } of entries) {
        const canvas = page.classList.contains('mobile-page-pan--enabled')
          ? page.querySelector('.mobile-page-pan-canvas') : null;
        const desktopLayout = desktop.matches || Boolean(canvas);
        const localAnchor = canvas || pinnedStage;
        if (module.parentElement !== (localAnchor || strip)) (localAnchor || strip).append(module);
        module.classList.toggle('statistic-module--mobile-stacked', !desktopLayout && definition.mobile?.stacked === true);
        const settings = desktopLayout ? definition : { ...definition, ...definition.mobile };
        for (const { property, value, item, part } of parts) {
          if (!['text', 'description'].includes(property)) continue;
          const mobileValue = item.mobileValue;
          const text = String(!desktopLayout ? mobileValue ?? value : value)
            .replace(/<br\s*\/?\s*>/gi, '\n');
          if (part.textContent !== text) part.textContent = text;
        }
        const mobileLeft = !desktopLayout && definition.mobile?.left != null;
        const mobileTop = !desktopLayout && definition.mobile?.top != null;
        const rightAligned = settings.right != null && !mobileLeft;
        const bottomAligned = settings.bottom != null && !mobileTop;
        const bounds = (canvas || anchor).getBoundingClientRect();
        const percentage = (value, fallback) => Number.parseFloat(value ?? fallback) / 100;
        module.classList.toggle('statistic-module--auto-width', settings.width == null);
        if (!localAnchor) {
          module.style.left = rightAligned ? 'auto' : `${bounds.left - stripBounds.left + bounds.width * percentage(settings.left, 12)}px`;
          module.style.right = rightAligned ? `${stripBounds.right - bounds.right + bounds.width * percentage(settings.right, 0)}px` : 'auto';
          module.style.top = bottomAligned ? 'auto' : `${bounds.top - stripBounds.top + bounds.height * percentage(settings.top, 12)}px`;
          module.style.bottom = bottomAligned ? `${stripBounds.bottom - bounds.bottom + bounds.height * percentage(settings.bottom, 0)}px` : 'auto';
        } else {
          module.style.left = rightAligned ? 'auto' : (settings.left ?? '12%');
          module.style.right = rightAligned ? settings.right : 'auto';
          module.style.top = bottomAligned ? 'auto' : (settings.top ?? '12%');
          module.style.bottom = bottomAligned ? settings.bottom : 'auto';
        }
        module.style.width = settings.width ?? 'max-content';
        const anchorImage = anchor.querySelector('.page-image, .filmstrip-slide');
        if (!anchorImage || (anchorImage.complete && anchorImage.naturalWidth > 0)) {
          module.dataset.positioned = 'true';
        }
        const length = desktopLayout ? viewport.width : viewport.height;
        const distance = desktopLayout ? viewport.left - bounds.left : viewport.top - bounds.top;
        const override = settings.parallax == null ? NaN : Number(settings.parallax);
        const parallaxStrength = reducedMotion.matches ? 0 :
          Number.isFinite(override) ? override : defaultParallax;
        const displacement = distance * parallaxStrength;
        const offset = Number.isFinite(override) ? displacement : Math.max(-80, Math.min(80, displacement));
        setParallaxTranslation(module, offset, desktopLayout, Boolean(localAnchor) || reducedMotion.matches);
        const moduleBounds = module.getBoundingClientRect();
        const position = desktopLayout ? moduleBounds.left - viewport.left : moduleBounds.top - viewport.top;
        const progress = clamp((length * 0.95 - position) / (length * 0.5));
        module.dataset.progress = String(progress);
        parts.forEach(({ property, value, item, part }, index) => {
          if (property === 'number') {
            const partPosition = part.getBoundingClientRect().top - viewport.top;
            const mobileProgress = clamp((viewport.height * 0.9 - partPosition) / (viewport.height * 0.15));
            const counter = part.firstElementChild;
            const countProgress = !reducedMotion.matches
              ? desktopLayout ? clamp(progress * parts.length - index) : mobileProgress
              : 1;
            const configuredSpeed = Number(settings.countUpSpeed);
            const countUpSpeed = Number.isFinite(configuredSpeed) && configuredSpeed > 0 ? configuredSpeed : 1;
            const countFraction = clamp(countProgress * countUpSpeed);
            const text = `${item.prefix || ''}${Math.round(value * countFraction)}`;
            if (counter.textContent !== text) counter.textContent = text;
            counter.style.opacity = String(countFraction);
            counter.style.transform = `translateY(${(1 - countFraction) * 24}px)`;
          }
        });
      }
    };
    container.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    desktop.addEventListener('change', update);
    reducedMotion.addEventListener('change', update);
    const observer = new ResizeObserver(update);
    observer.observe(container);
    for (const { module } of entries) observer.observe(module);
    for (const { anchor, module } of entries) {
      const anchorImage = anchor.querySelector('.page-image, .filmstrip-slide');
      if (!anchorImage || (anchorImage.complete && anchorImage.naturalWidth > 0)) continue;
      const revealAfterImageLoad = () => requestAnimationFrame(update);
      anchorImage.addEventListener('load', revealAfterImageLoad, { once: true });
      anchorImage.addEventListener('error', () => {
        module.dataset.positioned = 'true';
        revealAfterImageLoad();
      }, { once: true });
    }
    update();
  },
};

export default statisticModule;